import { cloneDeep, get, map } from 'lodash';
import Big from 'big.js';
import { PropTypes } from 'prop-types';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { FaStore, FaTags } from 'react-icons/fa';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import InlineEditTable from '../../../core/components/InlineEditTable/InlineEditTable';
import PageActions from '../../../core/components/PageActions';
import {
  addPermissionRefs,
  findPermissionRef,
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { PermissionKeys } from '../../../core/components/UserPermissions';
import {
  fetchPurchaseOrders as fetchPurchaseOrdersAction,
  fetchPurchaseOrder,
  editPurchaseOrder,
  addFile,
  deleteFile,
} from '../actions';
import { PurchaseOrder, PurchaseOrderStatus } from '../models';
import { selectPurchaseOrderInEdit } from '../purchaseOrderReducer';
import StatusButton from './StatusButton';
import moment from 'moment';
import { formatOrderDate } from '../helpers';
import { FilterPlacement, PageFilters } from '../../../core/components/PageFilters';
import { getApiStatus } from '../../../core/utils';
import { selectManagerSiteId } from '../../settings/reducer';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import DropdownMenuPortal from '../../tasks/WorkOrders/QuickTasks/components/DropdownMenuPortal';
import { parseNumber, formatNumber } from '../../../utils/number-util';
import { requiredInput } from '../../../core/formValidation';
import PurchaseOrderItemFields from './PurchaseOrderItemFields';
import * as toastMessages from '../../../core/toastMessages';
import { useOptimisticList } from '../../../core/hooks/useOptimisticList';
import { Drawer, DrawerContainer, DrawerControls } from '../../../core/components/Drawer';
import PurchaseOrderFormWrapper from './PurchaseOrderFormWrapper';
import PurchaseOrderDrawerContent from './PurchaseOrderDrawerContent';
import Loader from '../../../core/components/Loader';

const toFilterDate = (value) => {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

const isFilterSelected = (currentValue, optionValue) =>
  String(currentValue) === String(optionValue);

const InlineTotalEditor = ({ rowData, updatePurchaseOrder, formatItems, intl }) => {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const raw = rowData._raw || {};
  const methods = useForm({
    defaultValues: {
      items: formatItems(raw.items),
      taxAmount: `${parseNumber(raw.taxAmount || 0)}`,
      shippingAmount: `${parseNumber(raw.shippingAmount || 0)}`,
      discount: `${parseNumber(raw.discount || 0)}`,
    },
  });
  const { control, reset, getValues } = methods;
  const items = useWatch({ control, name: 'items' }) || [];
  const taxAmount = useWatch({ control, name: 'taxAmount' }) || '0';
  const shippingAmount = useWatch({ control, name: 'shippingAmount' }) || '0';
  const discount = useWatch({ control, name: 'discount' }) || '0';

  React.useEffect(() => {
    if (!open) return;
    reset({
      items: formatItems(raw.items),
      taxAmount: `${parseNumber(raw.taxAmount || 0)}`,
      shippingAmount: `${parseNumber(raw.shippingAmount || 0)}`,
      discount: `${parseNumber(raw.discount || 0)}`,
    });
  }, [open, raw, reset, formatItems]);

  const itemCosts = (items || []).map((item) => {
    if (!item || Number.isNaN(Number(item.quantity) * parseNumber(item.price || ''))) {
      return 0;
    }
    return Number(item.quantity || 0) * parseNumber(item.price || 0);
  });
  const subtotal = itemCosts.reduce((acc, val) => acc + val, 0);
  const total =
    subtotal +
    parseNumber(taxAmount || 0) +
    parseNumber(shippingAmount || 0) -
    parseNumber(discount || 0);

  const save = () => {
    const values = getValues();
    updatePurchaseOrder(rowData, {
      items: values.items || [],
      taxAmount: values.taxAmount,
      shippingAmount: values.shippingAmount,
      discount: values.discount,
    });
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn btn-link p-0"
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          textDecoration: 'none',
          width: '100%',
          textAlign: 'left',
          fontSize: 'inherit',
          color: '#0f172a',
        }}
      >
        {rowData.total || '$0.00'}
      </button>
      <DropdownMenuPortal
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        minWidth={250}
        maxWidth={350}
        align="right"
        offsetX={-60}
      >
        <FormProvider {...methods}>
          <div
            style={{
              padding: '8px',
              minWidth: 250,
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="form-group">
              <label htmlFor="items" className="mb-4">
                {intl.formatMessage({
                  id: 'purchaseOrderItems',
                  defaultMessage: 'Purchase Order Items',
                })}
              </label>
              <PurchaseOrderItemFields name="items" />
            </div>
            <div className="form-group purchase-order-row">
              <div>
                <small htmlFor="taxAmount" className="form-text text-muted text-left">
                  {intl.formatMessage({ id: 'tax', defaultMessage: 'Tax' })}
                </small>
                <Controller
                  name="taxAmount"
                  control={control}
                  render={({ field }) =>
                    requiredInput({
                      input: field,
                      className: 'form-control',
                      id: 'taxAmount',
                      type: 'text',
                      commas: 'true',
                      decimal: '2',
                      placeholder: '$0.00',
                      ariaDescribedBy: 'taxAmount',
                    })
                  }
                />
              </div>
              <div>
                <small htmlFor="shippingAmount" className="form-text text-muted text-left">
                  {intl.formatMessage({ id: 'shipping', defaultMessage: 'Shipping' })}
                </small>
                <Controller
                  name="shippingAmount"
                  control={control}
                  render={({ field }) =>
                    requiredInput({
                      input: field,
                      className: 'form-control',
                      id: 'shippingAmount',
                      type: 'text',
                      decimal: '2',
                      commas: 'true',
                      ariaDescribedBy: 'shippingCost',
                      placeholder: '$0.00',
                    })
                  }
                />
              </div>
              <div>
                <small htmlFor="discount" className="form-text text-muted text-left">
                  {intl.formatMessage({ id: 'discount', defaultMessage: 'Discount' })}
                </small>
                <Controller
                  name="discount"
                  control={control}
                  render={({ field }) =>
                    requiredInput({
                      input: field,
                      className: 'form-control',
                      id: 'discount',
                      type: 'text',
                      decimal: '2',
                      commas: 'true',
                      ariaDescribedBy: 'discount',
                      placeholder: '$0.00',
                    })
                  }
                />
              </div>
            </div>
            <hr />
            <div className="form-group form-purchase-order-totals">
              <div>SubTotal: $ {formatNumber(Big(subtotal).toFixed(2))}</div>
              <div>
                Total: $ {Number.isNaN(total) ? '0.00' : formatNumber(Big(total).toFixed(2))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button type="button" className="button inverted" onClick={() => setOpen(false)}>
                {intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
              </button>
              <button type="button" className="button primary" onClick={save}>
                {intl.formatMessage({ id: 'save', defaultMessage: 'Save' })}
              </button>
            </div>
          </div>
        </FormProvider>
      </DropdownMenuPortal>
    </div>
  );
};

const InlineSelect = ({ rowData, value, options, onSelect, placeholder }) => {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const label = value || placeholder || '-';
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn btn-link p-0"
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          textDecoration: 'none',
          width: '100%',
          textAlign: 'left',
          fontSize: 'inherit',
          color: '#0f172a',
        }}
      >
        {label}
      </button>
      <DropdownMenuPortal
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        minWidth={200}
      >
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            className={`dropdown-item${
              String(opt.value) === String(opt.selectedValue) ? ' active' : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              if (opt.value !== opt.selectedValue) onSelect(opt.value);
            }}
          >
            {opt.label}
          </button>
        ))}
      </DropdownMenuPortal>
    </div>
  );
};

const InlineTextEdit = ({ rowData, value, placeholder, onSave }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value || '');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (!editing) {
      setDraft(value || '');
    }
  }, [value, editing]);

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== (value || '')) {
      onSave(draft);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="form-control"
        style={{ minWidth: 140, height: 30, padding: '2px 6px' }}
        value={draft}
        placeholder={placeholder}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(false);
            setDraft(value || '');
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="btn btn-link p-0"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      style={{
        textDecoration: 'none',
        width: '100%',
        textAlign: 'left',
        fontSize: 'inherit',
        color: '#0f172a',
      }}
    >
      {value || placeholder || '-'}
    </button>
  );
};

export const PurchaseOrderTable = ({
  purchaseOrders,
  pageInfo,
  fromDate,
  signal,
  showDialog,
  untilDate,
  setFromDate,
  setUntilDate,
  fetchPurchaseOrders,
  receivePurchaseOrder,
  unreceivePurchaseOrder,
  categories,
  vendors,
  permissions,
  userGroup,
}) => {
  const {
    localList: localOrders,
    updateItem: updateLocalOrder,
    restoreItem: restoreLocalOrder,
    listRef: localOrdersRef,
  } = useOptimisticList(purchaseOrders, {
    compareFields: (order, local) => {
      if (order.memo !== local.memo) return true;
      if (order.trackingNumber !== local.trackingNumber) return true;
      if (order.categoryId !== local.categoryId) return true;
      if (order.vendorId !== local.vendorId) return true;
      if (order.taxAmount !== local.taxAmount) return true;
      if (order.shippingAmount !== local.shippingAmount) return true;
      if (order.discount !== local.discount) return true;
      if (order.receivedAt !== local.receivedAt) return true;
      if ((order.items || []).length !== (local.items || []).length) return true;
      return false;
    },
  });
  const [currentFilters, setCurrentFilters] = useState({
    currentCategory: 'All',
    currentVendor: 'All',
    currentStatus: 'All',
  });
  const [sortQuery, setSortQuery] = useState(null);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const formRef = useRef(null);
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => getApiStatus(state, 'FETCH_PURCHASE_ORDERS'));
  const isSaving = useSelector((state) => getApiStatus(state, 'EDIT_PURCHASE_ORDER'));
  const intl = useIntl();
  const handleApiError = React.useCallback((err) => {
    if (!err?.statusCode && !err?.responseStatus) {
      toastMessages.apiError(err);
    }
  }, []);
  const siteId = useSelector(selectManagerSiteId);
  const multiSite = useSelector(selectMultiSiteSelection);
  const purchaseOrderInEdit = useSelector(selectPurchaseOrderInEdit);
  const updateFilter = React.useCallback((key, values) => {
    setCurrentFilters((prev) => ({
      ...prev,
      [key]: values[0] || 'All',
    }));
  }, []);
  const purchaseOrderFilterConfigs = useMemo(
    () => [
      {
        id: 'dateRange',
        type: 'dateRange',
        name: intl.formatMessage({ id: 'date' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <i className="uil uil-calendar-alt" />,
        startDate: toFilterDate(fromDate),
        endDate: toFilterDate(untilDate),
        native: true,
        onChange: ([startDate, endDate]) => {
          setFromDate(startDate ? moment(startDate) : fromDate);
          setUntilDate(endDate ? moment(endDate) : untilDate);
        },
      },
      {
        id: 'currentStatus',
        type: 'select',
        name: intl.formatMessage({ id: 'status' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        multiSelect: false,
        icon: <FaRegCircleCheck />,
        options: [
          { value: PurchaseOrderStatus.All, text: intl.formatMessage({ id: 'all' }) },
          { value: PurchaseOrderStatus.Received, text: 'Received' },
          { value: PurchaseOrderStatus.Open, text: 'Not Received' },
        ].map((option) => ({
          ...option,
          selected: isFilterSelected(currentFilters.currentStatus, option.value),
        })),
        onChange: (values) => updateFilter('currentStatus', values),
      },
      {
        id: 'currentCategory',
        type: 'select',
        name: intl.formatMessage({ id: 'category' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        multiSelect: false,
        icon: <FaTags />,
        options: [
          { value: 'All', text: intl.formatMessage({ id: 'all' }) },
          ...categories.map((category) => ({
            value: category.name,
            text: category.name,
          })),
        ].map((option) => ({
          ...option,
          selected: isFilterSelected(currentFilters.currentCategory, option.value),
        })),
        onChange: (values) => updateFilter('currentCategory', values),
      },
      {
        id: 'currentVendor',
        type: 'select',
        name: intl.formatMessage({ id: 'vendor' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        multiSelect: false,
        icon: <FaStore />,
        options: [
          { value: 'All', text: intl.formatMessage({ id: 'all' }) },
          ...vendors.map((vendor) => ({
            value: vendor.name,
            text: vendor.name,
          })),
        ].map((option) => ({
          ...option,
          selected: isFilterSelected(currentFilters.currentVendor, option.value),
        })),
        onChange: (values) => updateFilter('currentVendor', values),
      },
    ],
    [
      categories,
      currentFilters,
      fromDate,
      intl,
      setFromDate,
      setUntilDate,
      untilDate,
      updateFilter,
      vendors,
    ]
  );
  const getQueryParams = (page = 0) => {
    const [from, until] = [fromDate.format('YYYY-MM-DD'), untilDate.format('YYYY-MM-DD')];
    return { page, from, until, signal, currentFilters, sortQuery };
  };

  const handleFilterChanges = () => {
    if (permissions[PermissionKeys.viewPurchaseOrdersTab]) {
      fetchPurchaseOrders(getQueryParams());
    }
  };

  useEffect(handleFilterChanges, [
    currentFilters,
    sortQuery,
    fromDate,
    untilDate,
    siteId,
    multiSite,
  ]);

  const computeOrderTotal = (order) => {
    const items = Array.isArray(order?.items) ? order.items : [];
    const itemTotal = items.reduce((acc, item) => {
      const qty = Number(item?.quantity || 0);
      const price = parseNumber(item?.price || item?.cost || item?.amount || 0);
      return acc + qty * price;
    }, 0);
    const tax = parseNumber(order?.taxAmount || 0);
    const shipping = parseNumber(order?.shippingAmount || 0);
    const discount = parseNumber(order?.discount || 0);
    return itemTotal + tax + shipping - discount;
  };
  // Update drawer when purchaseOrderInEdit changes (after fetchPurchaseOrder completes)
  useEffect(() => {
    if (
      purchaseOrderInEdit &&
      drawerExpanded &&
      selectedPurchaseOrder &&
      purchaseOrderInEdit.id === selectedPurchaseOrder?.id
    ) {
      setSelectedPurchaseOrder(new PurchaseOrder(cloneDeep(purchaseOrderInEdit)));
    }
  }, [purchaseOrderInEdit, drawerExpanded, selectedPurchaseOrder]);

  const getTableData = () => {
    let option = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return map(localOrders, (pos) => {
      const totalValue =
        pos.total !== null && pos.total !== undefined ? pos.total : computeOrderTotal(pos);
      return {
        _id: pos.id,
        _raw: pos,
        poNumber: pos.orderNumber,
        location: pos.siteName,
        _memo: pos.memo,
        category: pos.categoryName,
        vendor: pos.vendorName,
        _trackingNumber: pos.trackingNumber,
        total: `$${Number(totalValue || 0).toLocaleString(undefined, option)}`,
        _receivedAt: pos.receivedAt,
      };
    });
  };

  const triggerFetchPurchaseOrders = (sortedQuery) => {
    if (get(sortedQuery, 'key', false)) {
      setSortQuery(sortedQuery);
    }
  };

  const getPageActions = () => {
    const queryParams = getQueryParams();

    const callback = () => {
      return fetchPurchaseOrdersAction(queryParams);
    };

    return [
      {
        dialogType: 'ADD_PURCHASE_ORDER',
        permissionKey: addPermissionRefs.expense,
        text: intl.formatMessage({ id: 'addPurchaseOrder' }),
        dialogProps: { isNew: true, callback },
      },
      {
        dialogType: 'ADD_VENDOR',
        permissionKey: addPermissionRefs.expense_vendor,
        text: intl.formatMessage({ id: 'addVendor' }),
        permissionKey: addPermissionRefs.expense,
      },
    ];
  };

  const getPurchaseOrdersPage = (page) => {
    if (findPermissionRef(userGroup.permissions, viewPermissionRefs.expense)) {
      dispatch(fetchPurchaseOrdersAction(getQueryParams(page)));
    }
  };

  const handleOpenDrawer = (purchaseOrder) => {
    setDrawerExpanded(true);
    setSelectedPurchaseOrder(purchaseOrder);
  };

  const handleCloseDrawer = () => {
    setDrawerExpanded(false);
    setSelectedPurchaseOrder(null);
    setIsEditing(false);
  };

  const initializeDatatable = () => {
    const refreshOrders = () => {
      fetchPurchaseOrders(getQueryParams(pageInfo.page));
    };

    const formatItems = (items) =>
      (items || []).map((item) => ({
        name: item?.name || '',
        quantity: Number(item?.quantity || 0),
        price: `${parseNumber(item?.price || item?.cost || item?.amount || 0)}`,
      }));

    const normalizeItems = (items) =>
      (items || []).map((item) => {
        const qty = parseInt(item?.quantity, 10);
        return {
          name: item?.name || '',
          quantity: Number.isNaN(qty) ? 0 : qty,
          price: `${parseNumber(item?.price || 0)}`,
        };
      });

    const toNumberOrNull = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const num = Number(value);
      return Number.isNaN(num) ? null : num;
    };

    const buildPayload = (rowData, patch) => {
      const raw = rowData._raw || {};
      const nextItems =
        patch.items !== undefined ? normalizeItems(patch.items) : formatItems(raw.items);
      return {
        id: raw.id,
        categoryId: toNumberOrNull(
          patch.categoryId !== undefined ? patch.categoryId : raw.categoryId
        ),
        discount: `${parseNumber(patch.discount ?? raw.discount ?? 0)}`,
        employeeId: toNumberOrNull(
          patch.employeeId !== undefined ? patch.employeeId : raw.employeeId
        ),
        items: nextItems,
        orderDate: formatOrderDate(patch.orderDate ?? raw.orderDate),
        shippingAmount: `${parseNumber(patch.shippingAmount ?? raw.shippingAmount ?? 0)}`,
        taxAmount: `${parseNumber(patch.taxAmount ?? raw.taxAmount ?? 0)}`,
        trackingNumber: patch.trackingNumber ?? raw.trackingNumber ?? '',
        memo: patch.memo ?? raw.memo ?? '',
        vendorId: toNumberOrNull(patch.vendorId !== undefined ? patch.vendorId : raw.vendorId),
      };
    };

    const updatePurchaseOrder = (rowData, patch) => {
      if (!rowData?._raw?.id) return;
      const previous = localOrdersRef.current.find((order) => order.id === rowData._raw?.id);
      const previousSnapshot = previous
        ? {
            ...previous,
            _raw: { ...(previous._raw || {}) },
            items: previous.items ? [...previous.items] : previous.items,
          }
        : null;
      const payload = buildPayload(rowData, patch);
      const raw = rowData._raw || {};
      const nextTotal = computeOrderTotal({
        ...raw,
        ...payload,
        items: payload.items,
      });
      payload.total = `${nextTotal}`;
      const categoryMatch = (categories || []).find(
        (cat) => String(cat.id) === String(payload.categoryId)
      );
      const vendorMatch = (vendors || []).find(
        (ven) => String(ven.id) === String(payload.vendorId)
      );
      updateLocalOrder(payload.id, {
        ...payload,
        categoryName: categoryMatch ? categoryMatch.name : raw.categoryName,
        vendorName: vendorMatch ? vendorMatch.name : raw.vendorName,
      });
      editPurchaseOrder(payload, undefined, (err) => {
        handleApiError(err);
        if (!previousSnapshot) return;
        restoreLocalOrder(previousSnapshot);
      });
    };

    const headers = [
      { title: intl.formatMessage({ id: 'poNumber' }), keyName: 'poNumber' },
      { title: intl.formatMessage({ id: 'location' }), keyName: 'location' },
      {
        title: intl.formatMessage({ id: 'memo' }),
        keyName: '_memo',
        cell: {
          component: ({ rowData }) => (
            <InlineTextEdit
              rowData={rowData}
              value={rowData._memo}
              placeholder={intl.formatMessage({ id: 'memo' })}
              onSave={(next) => updatePurchaseOrder(rowData, { memo: next })}
            />
          ),
        },
      },
      {
        title: intl.formatMessage({ id: 'category' }),
        keyName: 'category',
        cell: {
          component: ({ rowData }) => (
            <InlineSelect
              rowData={rowData}
              value={rowData.category}
              placeholder={intl.formatMessage({ id: 'category' })}
              options={(categories || []).map((cat) => ({
                value: cat.id,
                label: cat.name,
                selectedValue: rowData._raw?.categoryId,
              }))}
              onSelect={(next) => updatePurchaseOrder(rowData, { categoryId: next })}
            />
          ),
        },
      },
      {
        title: intl.formatMessage({ id: 'vendor' }),
        keyName: 'vendor',
        cell: {
          component: ({ rowData }) => (
            <InlineSelect
              rowData={rowData}
              value={rowData.vendor}
              placeholder={intl.formatMessage({ id: 'vendor' })}
              options={(vendors || []).map((ven) => ({
                value: ven.id,
                label: ven.name,
                selectedValue: rowData._raw?.vendorId,
              }))}
              onSelect={(next) => updatePurchaseOrder(rowData, { vendorId: next })}
            />
          ),
        },
      },
      {
        title: intl.formatMessage({ id: 'trackingNumber' }),
        keyName: '_trackingNumber',
        cell: {
          component: ({ rowData }) => (
            <InlineTextEdit
              rowData={rowData}
              value={rowData._trackingNumber}
              placeholder={intl.formatMessage({ id: 'trackingNumber' })}
              onSave={(next) => updatePurchaseOrder(rowData, { trackingNumber: next })}
            />
          ),
        },
      },
      {
        title: intl.formatMessage({ id: 'total' }),
        keyName: 'total',
        cell: {
          component: ({ rowData }) => (
            <InlineTotalEditor
              rowData={rowData}
              updatePurchaseOrder={updatePurchaseOrder}
              formatItems={formatItems}
              intl={intl}
            />
          ),
        },
      },
      {
        title: intl.formatMessage({ id: 'status' }),
        keyName: '_receivedAt',
        cell: {
          component: StatusButton,
          mixIns: {
            receive: receivePurchaseOrder,
            unreceive: unreceivePurchaseOrder,
            showDialogs: showDialog,
            disabled: !permissions[PermissionKeys.receivePurchaseOrders],
          },
        },
      },
    ];

    const paginationInfo = {
      ...pageInfo,
      goToPage: getPurchaseOrdersPage,
    };

    return {
      data: getTableData(),
      headers,
      paginationInfo,
      scrolling: true,
      fetchSortedData: triggerFetchPurchaseOrders,
      rowClickInfo: {
        callback: (row) => {
          const fullPurchaseOrder = purchaseOrders.find((po) => po.id === row._id);
          handleOpenDrawer(fullPurchaseOrder || row);
        },
      },
    };
  };

  const tableConfig = useMemo(
    () => initializeDatatable(),
    [
      localOrders,
      categories,
      vendors,
      intl,
      pageInfo,
      localOrdersRef,
      updateLocalOrder,
      restoreLocalOrder,
      editPurchaseOrder,
      handleApiError,
      fetchPurchaseOrders,
      getQueryParams,
      computeOrderTotal,
      receivePurchaseOrder,
      unreceivePurchaseOrder,
      showDialog,
      permissions,
    ]
  );

  const navigationInfo = {
    navigationProperty: '_id',
    path: `/${params.site}/purchases/:id`,
  };

  return (
    <div className="purchase-orders">
      <DrawerContainer>
        <div>
          <div className="table-buttons">
            <div className="filters-actions-container">
              <PageFilters filters={purchaseOrderFilterConfigs} maxChicletFilters={4} />
              <PageActions actionFunction={showDialog} actions={getPageActions()} />
            </div>
          </div>
          {isLoading ? (
            <div className="container-fluid">
              <Loader />
            </div>
          ) : (
            <InlineEditTable
              api=""
              columns={tableConfig.headers}
              rows={tableConfig.data}
              paginationInfo={tableConfig.paginationInfo}
              isLoading={isLoading}
              paginateLocally={false}
              onRowClick={(row) => {
                const order = localOrders.find((o) => o.id === row._id);
                if (order) {
                  setSelectedPurchaseOrder(new PurchaseOrder(cloneDeep(order)));
                  setDrawerExpanded(true);
                  setIsEditing(false);
                }
              }}
            />
          )}
        </div>
        <Drawer
          expanded={drawerExpanded}
          expandedWidth={50}
          onBackdropClick={handleCloseDrawer}
          overlay
        >
          <div className="flex space-between">
            <div>
              <DrawerControls closeFunction={handleCloseDrawer} maximized={false} />
            </div>
            {selectedPurchaseOrder && (
              <div className="align-right">
                {!isEditing && (
                  <>
                    <button
                      className="button primary inverted mr-2 ml-2"
                      onClick={() => {
                        showDialog('DELETE_PURCHASE_ORDER', {
                          purchaseOrder: selectedPurchaseOrder,
                          onConfirm: () => {
                            handleCloseDrawer();
                          },
                        });
                      }}
                    >
                      {intl.formatMessage({ id: 'delete' })}
                    </button>
                    <button
                      className="button primary"
                      onClick={() => {
                        setIsEditing(true);
                      }}
                    >
                      {intl.formatMessage({ id: 'edit' })}
                    </button>
                  </>
                )}
                {isEditing && (
                  <>
                    <button
                      className="button primary inverted mr-2"
                      onClick={() => {
                        setIsEditing(false);
                      }}
                    >
                      {intl.formatMessage({ id: 'cancel' })}
                    </button>
                    <button
                      className="button primary"
                      style={{ minWidth: '80px' }}
                      onClick={() => {
                        if (formRef.current) {
                          formRef.current.submit();
                        }
                      }}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <Loader options={{ width: '24px', height: '24px' }} />
                      ) : (
                        intl.formatMessage({ id: 'save' })
                      )}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <hr />
          {selectedPurchaseOrder && !isEditing && (
            <div
              className="purchase-order-details"
              style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(100vh - 150px)' }}
            >
              <PurchaseOrderDrawerContent
                purchaseOrder={selectedPurchaseOrder}
                addFile={(data) => dispatch(addFile(data))}
                deleteFile={(data) => dispatch(deleteFile(data))}
                showDialog={showDialog}
                intl={intl}
              />
            </div>
          )}
          {selectedPurchaseOrder && isEditing && (
            <div
              className="purchase-order-edit"
              style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(100vh - 150px)' }}
            >
              <PurchaseOrderFormWrapper
                ref={formRef}
                initialValues={cloneDeep(selectedPurchaseOrder)}
                isNew={false}
                onSubmit={(values) => {
                  const formatItems = (items) => {
                    return items.map((item) => {
                      return {
                        name: item.name,
                        quantity: Number(item.quantity),
                        price: `${item.price}`,
                      };
                    });
                  };

                  const cleanedValues = {
                    id: values.id,
                    categoryId: Number(values.categoryId),
                    discount: `${values.discount || 0}`,
                    employeeId: Number(values.employeeId),
                    items: formatItems(values.items || []),
                    orderDate: formatOrderDate(values.orderDate),
                    shippingAmount: `${values.shippingAmount || 0}`,
                    taxAmount: `${values.taxAmount || 0}`,
                    trackingNumber: values.trackingNumber,
                    memo: values.memo,
                    vendorId: Number(values.vendorId),
                  };

                  const queryParams = getQueryParams();
                  const callback = () => {
                    setIsEditing(false);
                    // Refresh the table to show updated data
                    dispatch(fetchPurchaseOrdersAction(queryParams));
                    // Fetch the full purchase order to get all items and related data
                    dispatch(fetchPurchaseOrder(cleanedValues.id));
                  };
                  dispatch(editPurchaseOrder(cleanedValues, callback));
                }}
              />
            </div>
          )}
        </Drawer>
      </DrawerContainer>
    </div>
  );
};

PurchaseOrderTable.propTypes = {
  pageInfo: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}),
  userGroup: PropTypes.shape({}),
  showDialog: PropTypes.func.isRequired,
  purchaseOrders: PropTypes.arrayOf(PropTypes.shape(PurchaseOrder)),
  fetchPurchaseOrders: PropTypes.func.isRequired,
  receivePurchaseOrder: PropTypes.func.isRequired,
  unreceivePurchaseOrder: PropTypes.func.isRequired,
  signal: PropTypes.shape({}),
  fromDate: PropTypes.shape({ format: PropTypes.func }),
  untilDate: PropTypes.shape({ format: PropTypes.func }),
  setFromDate: PropTypes.func.isRequired,
  setUntilDate: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  vendors: PropTypes.arrayOf(PropTypes.shape({})),
};

export default PurchaseOrderTable;
